import { useQuery } from "@apollo/client";
import { FaClipboardList } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../../../context/AuthProvider";
import { GET_TIP_QUEUE } from "../../../../queries/tipQueue";
import { TipQueue, TipQueueEntry } from "../../../../types/TipQueue";
import { timeAgo } from "../../broadcasters/community/format";

// What's owed between shows, at a glance. Every banked tip, newest first,
// in a scrolling list. A tip opens the tipper's profile (broadcaster) or
// the queue (mods, who don't have the community pages).
const BankedTipsWidget = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data, loading } = useQuery(GET_TIP_QUEUE, {
    fetchPolicy: "cache-and-network",
    pollInterval: 10000,
  });
  const queue: TipQueue | undefined = data?.getTipQueue;
  const banked: TipQueueEntry[] = queue?.banked ?? [];

  const open = (entry: TipQueueEntry) =>
    navigate(
      user?.isBroadcaster
        ? `/community?tab=tippers&tipper=${encodeURIComponent(entry.username)}`
        : "/tip-queue",
    );

  return (
    <div className="card bg-base-100 border-2 border-accent w-full min-w-0">
      <div className="card-body p-3 sm:p-4 gap-2">
        <div className="flex items-center justify-between gap-2">
          <h3 className="font-semibold flex items-center gap-2">
            <FaClipboardList className="text-accent" />
            Banked tips{" "}
            <span className="text-base-content/60">({banked.length})</span>
          </h3>
          <Link to="/tip-queue" className="link link-accent text-xs">
            Open queue
          </Link>
        </div>
        {loading && !queue ? (
          <div className="loading loading-spinner loading-sm" />
        ) : banked.length === 0 ? (
          <div className="text-sm text-base-content/60">
            Nothing banked right now.
          </div>
        ) : (
          <ul className="flex flex-col gap-1 max-h-72 overflow-y-auto pr-1">
            {banked.map((entry) => (
              <li key={entry.id}>
                <button
                  className="w-full text-left rounded-box border border-accent/40 bg-accent/5 hover:bg-accent/15 px-3 py-2 flex flex-col gap-0.5"
                  onClick={() => open(entry)}
                  title={
                    user?.isBroadcaster
                      ? `Open ${entry.username}'s profile`
                      : "Open the tip queue"
                  }
                >
                  <span className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-semibold break-all">{entry.username}</span>
                    <span className="text-accent">{entry.itemName}</span>
                    <span className="text-xs text-base-content/60">
                      {entry.tokens.toLocaleString()} tokens
                    </span>
                  </span>
                  {entry.message && (
                    <span className="text-xs text-base-content/70 break-words">
                      {entry.message}
                    </span>
                  )}
                  <span className="text-xs text-base-content/50">
                    {timeAgo(entry.createdAt)}
                    {entry.bankedBy === "menu"
                      ? " · auto-banked"
                      : entry.bankedBy
                        ? ` · banked by ${entry.bankedBy}`
                        : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default BankedTipsWidget;
